// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
export type PayrollAuditDisplay = {
  label: string;
  description: string;
};

export type PayrollAuditEventLike = {
  action: string;
  actorDisplayName?: string;
  targetType?: string;
  targetIdentifier?: string;
  beforePayload?: Record<string, unknown>;
  afterPayload?: Record<string, unknown>;
  reason?: string;
  context?: Record<string, unknown>;
  classification?: {
    kind?: string;
    scope?: string;
    manualChange?: boolean;
    exceptionalAccess?: boolean;
  };
  createdAt?: string;
};

export type PayrollAuditPresentation = PayrollAuditDisplay & {
  scopeLabel: string;
  month: string;
  monthLabel: string;
  actorName: string;
  targetLabel: string;
  explanationLabel: string;
  explanation: string;
};

const knownEvents: Record<string, PayrollAuditDisplay> = {
  "timesheet_version.generated": {
    label: "Timesheet locked and forwarded",
    description: "A finalized timesheet version was stored for payroll.",
  },
  "timesheet_version.import_refreshed": {
    label: "Locked timesheet refreshed",
    description:
      "Imported work hours were refreshed while the payroll timesheet remained locked.",
  },
  "timesheet_version.released": {
    label: "Timesheet lock released",
    description: "A finalized payroll timesheet was reopened for correction.",
  },
  "timesheet_snapshot.imported": {
    label: "Work hours imported",
    description:
      "Finalized work hours were copied into payroll from a locked OpenProject timesheet.",
  },
  "timesheet_snapshot.synchronized": {
    label: "Work hours synchronized",
    description:
      "Payroll work hours were refreshed from a newer locked OpenProject timesheet.",
  },
  "timesheet_snapshot.locked": {
    label: "Timesheet snapshot locked",
    description:
      "A payroll timesheet snapshot was locked against further changes.",
  },
  "timesheet_snapshot.unlocked": {
    label: "Timesheet snapshot unlocked",
    description: "A payroll timesheet snapshot was reopened for changes.",
  },
  "payroll_profile.updated": {
    label: "Updated payroll profile",
    description: "An employee's current payroll settings were changed.",
  },
  "payroll_profile_period.created": {
    label: "Added payroll settings period",
    description:
      "A new effective-dated payroll period was added to an employee profile.",
  },
  "payroll_profile_period.updated": {
    label: "Updated payroll settings period",
    description: "An employee's effective-dated payroll settings were changed.",
  },
  "payroll_profile_period.deleted": {
    label: "Removed payroll settings period",
    description:
      "An effective-dated payroll period was removed from an employee profile.",
  },
  "pohoda_export.generated": {
    label: "Pohoda input exported",
    description: "A versioned Pohoda payroll input workbook was generated.",
  },
  "pohoda_export.created": {
    label: "Pohoda input exported",
    description: "A Pohoda payroll input workbook was generated.",
  },
  "payslip.uploaded": {
    label: "Uploaded payslips",
    description: "One or more payslip files were added to a payroll month.",
  },
  "payslip.manual_resolved": {
    label: "Matched payslip to employee",
    description:
      "A reviewed payslip was manually linked to an employee and payroll month.",
  },
  "payslip.field_corrected": {
    label: "Corrected payslip value",
    description:
      "A value extracted from a payslip was manually corrected with a reason.",
  },
  "payslip.duplicate_replaced": {
    label: "Replaced existing payslip",
    description: "A reviewed upload replaced an older usable payslip.",
  },
  "payslip.ignored": {
    label: "Removed payslip from review",
    description:
      "A reviewed payslip was excluded from the active payroll review.",
  },
  "payslips.duplicate_batch_ignored": {
    label: "Kept existing payslips",
    description:
      "Existing payslips were kept and a duplicate upload was removed from review.",
  },
  "payslips.cross_month_batch_recovered": {
    label: "Moved payslips to the correct month",
    description:
      "Payslips were assigned to the month printed in the PDF and removed from the wrong-month review.",
  },
  "payslip.confirmed": {
    label: "Confirmed payslip",
    description: "A reviewed employee payslip was confirmed.",
  },
  "payslips.bulk_confirmed": {
    label: "Confirmed ready payslips",
    description: "All payslips that passed review were confirmed together.",
  },
  "payslip.replaced": {
    label: "Replaced payslip",
    description: "An existing payslip file was replaced with a newer upload.",
  },
  "payslip.resolved": {
    label: "Resolved payslip issue",
    description:
      "A payslip matching or validation issue was marked as resolved.",
  },
  "balance_adjustment.created": {
    label: "Adjusted employee balance",
    description:
      "An employee balance was manually adjusted with an audit reason.",
  },
  "payroll_balance.adjusted": {
    label: "Adjusted employee balance",
    description:
      "An employee balance was manually adjusted with an audit reason.",
  },
  "balance_calculation.previewed": {
    label: "Calculated payroll balances",
    description: "A payroll balance calculation preview was generated.",
  },
  "balance_calculation.approved": {
    label: "Approved payroll balances",
    description: "Eligible payroll balance results were approved.",
  },
  "payroll_period.calculated": {
    label: "Payroll period calculated",
    description: "Payroll calculations completed for the selected month.",
  },
  "payroll_period.approved": {
    label: "Payroll period approved",
    description: "The payroll period was approved for completion.",
  },
  "payroll.value_correction_set": {
    label: "Payroll value corrected",
    description:
      "A payroll value was manually corrected with a recorded reason.",
  },
  "payroll.value_correction_revert": {
    label: "Payroll correction reverted",
    description:
      "A manual payroll correction was reverted to the source value.",
  },
  "payroll.value_correction_reaffirm": {
    label: "Payroll correction reaffirmed",
    description:
      "A manual payroll correction was reviewed against the latest source and reaffirmed.",
  },
  "payroll.value_correction_exceptional_set": {
    label: "Exceptional payroll correction",
    description:
      "An administrator used acknowledged exceptional access to correct a protected payroll value.",
  },
  "payroll.value_correction_exceptional_revert": {
    label: "Exceptional correction reverted",
    description:
      "An administrator used acknowledged exceptional access to revert a protected payroll correction.",
  },
  "payroll.value_correction_exceptional_reaffirm": {
    label: "Exceptional correction reaffirmed",
    description:
      "An administrator used acknowledged exceptional access to reaffirm a protected payroll correction.",
  },
  "balance_difference.accepted": {
    label: "Accepted payroll difference",
    description:
      "A reviewed payroll difference was accepted with a recorded reason.",
  },
  "balance_overtime.confirmed": {
    label: "Overtime confirmed",
    description:
      "A reviewed overtime difference was confirmed with a recorded reason.",
  },
  "payroll.month_closed": {
    label: "Closed payroll month",
    description: "The approved payroll month was closed.",
  },
  "payroll.month_reopened": {
    label: "Reopened payroll month",
    description: "A closed payroll month was reopened with a recorded reason.",
  },
  "payroll.workflow_advanced": {
    label: "Moved payroll to the next step",
    description: "The monthly payroll workflow moved to its next stage.",
  },
  "payroll.downstream_invalidated": {
    label: "Marked later payroll work as outdated",
    description:
      "A source change required later payroll calculations or approvals to be completed again.",
  },
};

const words = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const sentenceCase = (value: string) => {
  const normalized = words(value);
  return normalized
    ? `${normalized.charAt(0).toLocaleUpperCase()}${normalized.slice(1).toLocaleLowerCase()}`
    : "Payroll event";
};

const titleCase = (value: string) =>
  words(value)
    .toLocaleLowerCase()
    .replace(/\b\p{L}/gu, (character) => character.toLocaleUpperCase());

const textValue = (event: PayrollAuditEventLike, ...keys: string[]) => {
  const sources = [event.context, event.afterPayload, event.beforePayload];
  for (const key of keys) {
    for (const source of sources) {
      const value = source?.[key];
      if (value === undefined || value === null) continue;
      const normalized = String(value).trim();
      if (normalized) return normalized;
    }
  }
  return "";
};

const numericValue = (event: PayrollAuditEventLike, ...keys: string[]) => {
  const value = textValue(event, ...keys);
  if (!value) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

const plural = (count: number, singular: string, pluralForm = `${singular}s`) =>
  `${count} ${count === 1 ? singular : pluralForm}`;

const listValue = (event: PayrollAuditEventLike, ...keys: string[]) => {
  const sources = [event.context, event.afterPayload, event.beforePayload];
  for (const key of keys) {
    for (const source of sources) {
      const value = source?.[key];
      if (Array.isArray(value))
        return value.map((item) => String(item ?? "").trim()).filter(Boolean);
    }
  }
  return [];
};

const workflowStageLabels: Record<string, string> = {
  prepare: "Prepare records",
  pohoda: "Create Pohoda input",
  payslips: "Review payslips",
  reconcile: "Check balances & approve",
  approve: "Check balances & approve",
  close: "Close & email payroll",
};

const workflowStageLabel = (value: string) =>
  workflowStageLabels[value] || sentenceCase(value);

const profileFieldLabels: Record<string, string> = {
  employeeCode: "employee code",
  calculationEnabled: "payroll availability",
  contractType: "contract type",
  workloadPercent: "workload",
  hoursPerDay: "hours per day",
  annualHolidayEntitlement: "annual vacation allowance",
  validFromMonth: "start month",
  validToMonth: "end month",
};

const profileFieldLabel = (value: string) =>
  profileFieldLabels[value] || words(value).toLocaleLowerCase();

const joinLabels = (values: string[]) => {
  const labels = values.map(profileFieldLabel);
  if (labels.length <= 1) return labels[0] ?? "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;
};

const employeeIdentity = (
  event: PayrollAuditEventLike,
  employeeName: string,
) => {
  if (employeeName) return employeeName;
  const employeeId = numericValue(event, "userId", "employeeId");
  return employeeId ? `Employee #${employeeId}` : "";
};

const employeePossessive = (employee: string) =>
  employee ? `${employee}'s` : "an employee's";

const eventTitle = (
  event: PayrollAuditEventLike,
  display: PayrollAuditDisplay,
  employeeName: string,
  monthLabel: string,
) => {
  const employee = employeeIdentity(event, employeeName);
  const employeeSuffix = employee ? ` for ${employee}` : "";
  const possessive = employeePossessive(employee);
  const field = words(textValue(event, "fieldKey", "field")) || "payroll value";
  const check = titleCase(textValue(event, "checkKey")) || "Payroll";
  const changedFields = listValue(event, "changedFields");
  const fileCount = numericValue(event, "fileCount");
  const resultCount = numericValue(event, "results");
  const approvedRows = numericValue(event, "approvedRows");
  const confirmedCount = numericValue(event, "confirmedCount");
  const keptExistingCount = numericValue(event, "keptExistingCount");
  const recoveredCount = numericValue(event, "recoveredCount");
  const includedEmployeeCount = numericValue(event, "includedEmployeeCount");

  if (event.action === "timesheet_version.generated")
    return `Locked ${possessive} timesheet`;
  if (event.action === "timesheet_version.import_refreshed")
    return `Refreshed ${possessive} locked work hours`;
  if (event.action === "timesheet_version.released")
    return `Reopened ${possessive} timesheet`;
  if (event.action === "timesheet_snapshot.imported")
    return `Imported work hours${employeeSuffix}`;
  if (event.action === "timesheet_snapshot.synchronized")
    return `Updated work hours${employeeSuffix}`;
  if (event.action === "timesheet_snapshot.locked")
    return `Locked ${possessive} payroll timesheet`;
  if (event.action === "timesheet_snapshot.unlocked")
    return `Unlocked ${possessive} payroll timesheet`;
  if (event.action === "payroll_profile.updated")
    return changedFields.length === 1
      ? `Changed ${possessive} ${profileFieldLabel(changedFields[0]!)}`
      : `Updated ${possessive} payroll profile`;
  if (event.action === "payroll_profile_period.created")
    return `Added payroll settings period${employeeSuffix}`;
  if (event.action === "payroll_profile_period.updated")
    return changedFields.length === 1
      ? `Changed ${possessive} ${profileFieldLabel(changedFields[0]!)}`
      : `Updated payroll settings period${employeeSuffix}`;
  if (event.action === "payroll_profile_period.deleted")
    return `Removed payroll settings period${employeeSuffix}`;
  if (
    event.action === "pohoda_export.generated" ||
    event.action === "pohoda_export.created"
  )
    return includedEmployeeCount === undefined
      ? "Created Pohoda input"
      : `Created Pohoda input for ${plural(includedEmployeeCount, "employee")}`;
  if (event.action === "payslip.uploaded")
    return fileCount === undefined
      ? "Uploaded payslips"
      : `Uploaded ${plural(fileCount, "payslip PDF")}`;
  if (event.action === "payslip.manual_resolved")
    return `Matched ${possessive} payslip`;
  if (event.action === "payslip.field_corrected")
    return `Corrected ${field}${employeeSuffix}`;
  if (
    event.action === "payslip.duplicate_replaced" ||
    event.action === "payslip.replaced"
  )
    return `Replaced ${possessive} payslip`;
  if (event.action === "payslip.ignored")
    return `Removed ${possessive} payslip from review`;
  if (event.action === "payslips.duplicate_batch_ignored")
    return keptExistingCount === undefined
      ? "Kept existing payslips"
      : `Kept ${plural(keptExistingCount, "existing payslip")}`;
  if (event.action === "payslips.cross_month_batch_recovered")
    return recoveredCount === undefined
      ? "Moved payslips to the correct month"
      : `Moved ${plural(recoveredCount, "payslip")} to the correct month`;
  if (event.action === "payslip.confirmed")
    return `Confirmed ${possessive} payslip`;
  if (event.action === "payslips.bulk_confirmed")
    return confirmedCount === undefined
      ? "Confirmed ready payslips"
      : `Confirmed ${plural(confirmedCount, "payslip")}`;
  if (event.action === "payslip.resolved")
    return `Resolved ${possessive} payslip issue`;
  if (
    event.action === "balance_adjustment.created" ||
    event.action === "payroll_balance.adjusted"
  ) {
    const balanceType =
      words(textValue(event, "balanceType")).toLocaleLowerCase() || "balance";
    return `Adjusted ${possessive} ${balanceType}`;
  }
  if (event.action === "balance_calculation.previewed")
    return resultCount === undefined
      ? "Calculated payroll balances"
      : `Calculated balances for ${plural(resultCount, "employee")}`;
  if (event.action === "balance_calculation.approved")
    return approvedRows === undefined
      ? "Approved payroll balances"
      : `Approved ${plural(approvedRows, "employee balance")}`;
  if (event.action === "payroll_period.calculated")
    return monthLabel
      ? `Calculated ${monthLabel} payroll`
      : "Calculated payroll period";
  if (event.action === "payroll_period.approved")
    return monthLabel
      ? `Approved ${monthLabel} payroll`
      : "Approved payroll period";
  if (event.action === "payroll.value_correction_set")
    return `Corrected ${possessive} ${field}`;
  if (event.action === "payroll.value_correction_exceptional_set")
    return `Corrected ${possessive} ${field} with administrator access`;
  if (event.action === "payroll.value_correction_revert")
    return `Removed correction from ${possessive} ${field}`;
  if (event.action === "payroll.value_correction_exceptional_revert")
    return `Removed correction from ${possessive} ${field} with administrator access`;
  if (event.action === "payroll.value_correction_reaffirm")
    return `Kept correction for ${possessive} ${field}`;
  if (event.action === "payroll.value_correction_exceptional_reaffirm")
    return `Kept correction for ${possessive} ${field} with administrator access`;
  if (event.action === "balance_difference.accepted")
    return `Accepted ${check.toLocaleLowerCase()} difference${employeeSuffix}`;
  if (event.action === "balance_overtime.confirmed")
    return `Confirmed overtime${employeeSuffix}`;
  if (event.action === "payroll.workflow_advanced") {
    const toStage = textValue(event, "toStage");
    return toStage
      ? `Moved payroll to ${workflowStageLabel(toStage)}`
      : display.label;
  }
  if (event.action === "payroll.month_closed")
    return monthLabel ? `Closed ${monthLabel} payroll` : display.label;
  if (event.action === "payroll.month_reopened")
    return monthLabel ? `Reopened ${monthLabel} payroll` : display.label;
  if (event.action === "payroll.downstream_invalidated") {
    const fromStage = textValue(event, "fromStage");
    const workflowMovedBack = textValue(event, "workflowMovedBack") === "true";
    return fromStage && workflowMovedBack
      ? `Sent payroll back to ${workflowStageLabel(fromStage)}`
      : "Marked later payroll work as outdated";
  }

  return display.label;
};

export const formatPayrollMonth = (value: string) => {
  const normalized = value.trim();
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(normalized);
  if (!match) return normalized;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

export const formatAuditDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatAuditTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getPayrollAuditDisplay = (action: string): PayrollAuditDisplay => {
  const normalized = action.trim();
  const known = knownEvents[normalized];
  if (known) return known;

  const [subject, ...eventParts] = normalized.split(".");
  const subjectLabel = sentenceCase(subject || "payroll");
  const eventLabel = words(eventParts.join(" "));
  return {
    label: eventLabel
      ? `${subjectLabel} ${eventLabel.toLocaleLowerCase()}`
      : subjectLabel,
    description: eventLabel
      ? `A ${subjectLabel.toLocaleLowerCase()} event was recorded by the payroll system.`
      : "A technical payroll event was recorded.",
  };
};

const eventDescription = (
  event: PayrollAuditEventLike,
  display: PayrollAuditDisplay,
  employeeName: string,
  monthLabel: string,
) => {
  const employee = employeeIdentity(event, employeeName) || "The employee";
  const payrollMonth = monthLabel || "the selected payroll month";
  const versionNumber = numericValue(event, "versionNumber");
  const amount = textValue(event, "amount");
  const balanceType = words(textValue(event, "balanceType")) || "balance";
  const adjustmentType = words(textValue(event, "adjustmentType")) || "manual";
  const fileCount = numericValue(event, "fileCount") ?? 0;
  const recordCount = numericValue(event, "recordCount", "records") ?? 0;
  const failedCount = numericValue(event, "failedCount") ?? 0;
  const resultCount = numericValue(event, "results") ?? 0;
  const approvedRows = numericValue(event, "approvedRows");
  const includedEmployeeCount =
    numericValue(event, "includedEmployeeCount") ?? 0;

  if (event.action === "timesheet_version.generated") {
    return `${employee}'s timesheet for ${payrollMonth} was locked and stored${versionNumber ? ` as version ${versionNumber}` : ""} for payroll processing.`;
  }
  if (event.action === "timesheet_version.import_refreshed") {
    return `${employee}'s imported work hours for ${payrollMonth} were refreshed and kept locked${versionNumber ? ` as version ${versionNumber}` : ""}.`;
  }
  if (event.action === "timesheet_version.released") {
    return `${employee}'s timesheet lock for ${payrollMonth} was released so the work hours can be corrected.`;
  }
  if (event.action === "timesheet_snapshot.imported") {
    return `${employee}'s finalized work hours were imported from a locked OpenProject timesheet into ${payrollMonth} payroll.`;
  }
  if (event.action === "timesheet_snapshot.synchronized") {
    return `${employee}'s finalized work hours for ${payrollMonth} were refreshed from a newer locked OpenProject timesheet.`;
  }
  if (
    event.action === "balance_adjustment.created" ||
    event.action === "payroll_balance.adjusted"
  ) {
    return `${employee}'s ${balanceType} was adjusted${amount ? ` by ${amount}` : ""} for ${payrollMonth} (${adjustmentType}).`;
  }
  if (event.action === "payroll_profile.updated") {
    const changedFields = listValue(event, "changedFields");
    return changedFields.length
      ? `${employee}'s ${joinLabels(changedFields)} ${changedFields.length === 1 ? "was" : "were"} changed.`
      : `${employee}'s current payroll settings were changed.`;
  }
  if (event.action === "payroll_profile_period.created") {
    return `A payroll profile period beginning ${payrollMonth} was added for ${employee}.`;
  }
  if (event.action === "payroll_profile_period.updated") {
    return `The payroll profile period beginning ${payrollMonth} was changed for ${employee}.`;
  }
  if (event.action === "payroll_profile_period.deleted") {
    return `The payroll profile period beginning ${payrollMonth} was removed for ${employee}.`;
  }
  if (event.action === "payslip.uploaded") {
    const failureText =
      failedCount > 0
        ? ` ${plural(failedCount, "record")} require attention.`
        : " All created records passed the initial upload step.";
    return `${plural(fileCount, "PDF")} created ${plural(recordCount, "payslip record")} for ${payrollMonth}.${failureText}`;
  }
  if (event.action === "payslip.manual_resolved") {
    return `The reviewed payslip was manually linked to ${employee} for ${payrollMonth}.`;
  }
  if (event.action === "payslip.field_corrected") {
    const field = words(textValue(event, "field")) || "payslip value";
    const before = textValue(event, "beforeValue");
    const after = textValue(event, "afterValue", "value");
    const change =
      before || after
        ? ` from ${before || "not recorded"} to ${after || "not recorded"}`
        : "";
    return `${employee}'s ${field} was corrected${change} for ${payrollMonth}.`;
  }
  if (event.action === "payslip.duplicate_replaced") {
    return `A newer reviewed PDF replaced the existing payslip for ${employee} in ${payrollMonth}.`;
  }
  if (event.action === "payslip.ignored") {
    return `The payslip for ${employee} was removed from the active ${payrollMonth} review.`;
  }
  if (event.action === "payslips.duplicate_batch_ignored") {
    const keptExistingCount =
      numericValue(event, "keptExistingCount") ?? recordCount;
    return `${plural(keptExistingCount, "existing payslip")} for ${payrollMonth} stayed unchanged; the duplicate upload was removed in one audited action.`;
  }
  if (event.action === "payslips.cross_month_batch_recovered") {
    const recoveredCount = numericValue(event, "recoveredCount") ?? recordCount;
    const destinationMonth = textValue(event, "destinationMonth");
    return `${plural(recoveredCount, "payslip")} ${recoveredCount === 1 ? "was" : "were"} moved${destinationMonth ? ` to ${formatPayrollMonth(destinationMonth)}` : " to the correct payroll month"}; the wrong-month review records were removed in one audited action.`;
  }
  if (event.action === "payslip.confirmed") {
    return `${employee}'s payslip for ${payrollMonth} was reviewed and confirmed.`;
  }
  if (event.action === "payslips.bulk_confirmed") {
    const confirmedCount = numericValue(event, "confirmedCount") ?? 0;
    const excludedCount = numericValue(event, "excludedAttentionCount") ?? 0;
    return `${plural(confirmedCount, "ready payslip")} for ${payrollMonth} ${confirmedCount === 1 ? "was" : "were"} confirmed together${excludedCount ? `; ${plural(excludedCount, "payslip")} still ${excludedCount === 1 ? "needs" : "need"} review` : ""}.`;
  }
  if (event.action === "balance_calculation.previewed") {
    return `A balance calculation preview with ${plural(resultCount, "employee result")} was generated for ${payrollMonth}.`;
  }
  if (event.action === "balance_calculation.approved") {
    return `${approvedRows === undefined ? "Eligible balance results" : plural(approvedRows, "balance row")} for ${payrollMonth} were approved for the next payroll step.`;
  }
  if (
    event.action === "pohoda_export.generated" ||
    event.action === "pohoda_export.created"
  ) {
    return `Pohoda input${versionNumber ? ` version ${versionNumber}` : ""} was generated for ${payrollMonth} with ${plural(includedEmployeeCount, "employee")}.`;
  }
  if (
    [
      "payroll.value_correction_set",
      "payroll.value_correction_revert",
      "payroll.value_correction_reaffirm",
      "payroll.value_correction_exceptional_set",
      "payroll.value_correction_exceptional_revert",
      "payroll.value_correction_exceptional_reaffirm",
    ].includes(event.action)
  ) {
    const field = words(textValue(event, "fieldKey")) || "payroll value";
    const before = textValue(event, "beforeValueHours");
    const after = textValue(event, "afterValueHours");
    const change =
      before || after
        ? ` from ${before || "blank"} to ${after || "blank"} hours`
        : "";
    const exceptional = event.classification?.exceptionalAccess
      ? " using exceptional administrator access"
      : "";
    return `${employee}'s ${field} was updated${change} for ${payrollMonth}${exceptional}.`;
  }
  if (
    event.action === "balance_difference.accepted" ||
    event.action === "balance_overtime.confirmed"
  ) {
    const check = titleCase(textValue(event, "checkKey")) || "Payroll";
    const timesheetValue = textValue(event, "timesheetValue");
    const payslipValue = textValue(event, "payslipValue");
    const difference = textValue(event, "difference");
    const displayHours = (value: string) =>
      value ? `${value}h` : "not recorded";
    const comparison =
      timesheetValue || payslipValue || difference
        ? ` Timesheet: ${displayHours(timesheetValue)}. Payslip: ${displayHours(payslipValue)}. Difference: ${displayHours(difference)}.`
        : "";
    const decision =
      event.action === "balance_overtime.confirmed"
        ? "was confirmed"
        : "was accepted";
    return `${check} for ${employee} ${decision} in ${payrollMonth}.${comparison}`;
  }
  if (event.action === "payroll.month_closed") {
    return `${payrollMonth} payroll was closed after approval.`;
  }
  if (event.action === "payroll.month_reopened") {
    return `${payrollMonth} payroll was reopened for controlled changes.`;
  }
  if (event.action === "payroll.workflow_advanced") {
    const fromStage = textValue(event, "fromStage");
    const toStage = textValue(event, "toStage");
    return fromStage && toStage
      ? `${payrollMonth} payroll moved from ${workflowStageLabel(fromStage)} to ${workflowStageLabel(toStage)}.`
      : `${payrollMonth} payroll moved to the next step.`;
  }
  if (event.action === "payroll.downstream_invalidated") {
    const fromStage = textValue(event, "fromStage");
    const workflowMovedBack = textValue(event, "workflowMovedBack") === "true";
    return fromStage && workflowMovedBack
      ? `${payrollMonth} payroll returned to ${workflowStageLabel(fromStage)} because earlier information changed.`
      : `Later calculations or approvals for ${payrollMonth} were marked as outdated because earlier information changed.`;
  }
  return monthLabel
    ? `${display.description} Payroll month: ${monthLabel}.`
    : display.description;
};

const eventExplanation = (event: PayrollAuditEventLike, monthLabel: string) => {
  const reason = String(event.reason ?? "").trim();
  const payrollMonth = monthLabel || "the selected payroll month";
  if (
    reason.toLocaleLowerCase() ===
    "synchronized from locked openproject timesheet"
  ) {
    return {
      label: "Source confirmation",
      text: `The locked OpenProject timesheet was used as the finalized source for ${payrollMonth} payroll.`,
    };
  }
  if (reason) return { label: "Audit reason", text: reason };

  if (
    event.action === "timesheet_snapshot.imported" ||
    event.action === "timesheet_snapshot.synchronized"
  ) {
    return {
      label: "System explanation",
      text: `This event identifies the locked timesheet version used as the source for ${payrollMonth} payroll.`,
    };
  }
  if (event.action === "payslip.uploaded") {
    return {
      label: "System explanation",
      text: "This event records the upload batch and whether any created payslip records require review.",
    };
  }
  if (
    event.action === "pohoda_export.generated" ||
    event.action === "pohoda_export.created"
  ) {
    return {
      label: "System explanation",
      text: `This event records the versioned workbook generated from the finalized inputs for ${payrollMonth}.`,
    };
  }
  if (event.action.startsWith("balance_calculation.")) {
    return {
      label: "System explanation",
      text: `This event records the calculation state used for ${payrollMonth} payroll.`,
    };
  }
  return {
    label: "Audit note",
    text: "No additional reason was recorded for this event.",
  };
};

export const getPayrollAuditPresentation = (
  event: PayrollAuditEventLike,
): PayrollAuditPresentation => {
  const display = getPayrollAuditDisplay(event.action);
  const employeeName = textValue(event, "employeeName", "displayName");
  const employee = employeeIdentity(event, employeeName);
  const effectiveMonth = textValue(
    event,
    "month",
    "confirmedPeriod",
    "effectiveFrom",
  ).slice(0, 7);
  const monthLabel = formatPayrollMonth(effectiveMonth);
  const targetType = titleCase(event.targetType ?? "");
  const targetIdentifier = String(event.targetIdentifier ?? "").trim();
  const targetLabel = targetType
    ? `${targetType}${targetIdentifier ? ` #${targetIdentifier}` : ""}`
    : "Payroll system";
  const explanation = eventExplanation(event, monthLabel);
  const monthWideAction = [
    "payslip.uploaded",
    "payslips.bulk_confirmed",
    "payslips.duplicate_batch_ignored",
    "balance_calculation.previewed",
    "balance_calculation.approved",
    "pohoda_export.generated",
    "pohoda_export.created",
  ].includes(event.action);
  const scopeLabel =
    employee ||
    (event.classification?.scope === "month" || monthWideAction
      ? "All affected employees"
      : event.classification?.scope === "workflow"
        ? "Payroll workflow"
        : event.classification?.scope === "document"
          ? "Payslip record"
          : event.classification?.scope === "period"
            ? "Payroll settings period"
            : targetLabel);

  return {
    label: eventTitle(event, display, employeeName, monthLabel),
    description: eventDescription(event, display, employeeName, monthLabel),
    scopeLabel,
    month: effectiveMonth,
    monthLabel: monthLabel || "Not recorded",
    actorName: String(event.actorDisplayName ?? "").trim() || "System",
    targetLabel,
    explanationLabel: explanation.label,
    explanation: explanation.text,
  };
};

const positiveInteger = (value: string | null) =>
  value && /^[1-9]\d*$/.test(value) && Number.isSafeInteger(Number(value))
    ? Number(value)
    : undefined;

export const payrollAuditFiltersFromSearch = (
  search: string,
  level: OpenProjectPayrollAuditLevel,
): OpenProjectPayrollAuditFilter => {
  const params = new URLSearchParams(search);
  const month = params.get("month") ?? undefined;
  const fromMonth = params.get("fromMonth") ?? undefined;
  const throughMonth =
    params.get("throughMonth") ?? params.get("periodThrough") ?? undefined;
  const userId = positiveInteger(
    params.get("employeeId") ?? params.get("userId"),
  );
  const candidate = {
    level,
    ...(month
      ? { month }
      : {
          ...(fromMonth ? { fromMonth } : {}),
          ...(throughMonth ? { throughMonth } : {}),
        }),
    ...(userId ? { userId } : {}),
    ...(params.get("fieldKey") ? { fieldKey: params.get("fieldKey") } : {}),
    ...(params.get("eventKind") ? { eventKind: params.get("eventKind") } : {}),
    ...(params.get("scope") ? { scope: params.get("scope") } : {}),
  };
  const parsed = openProjectPayrollAuditFilterSchema.safeParse(candidate);
  if (parsed.success) return parsed.data;
  const withoutDependentField = {
    ...candidate,
    fieldKey: undefined,
  };
  const fallback = openProjectPayrollAuditFilterSchema.safeParse(
    withoutDependentField,
  );
  return fallback.success ? fallback.data : { level };
};

export const payrollAuditFilterSummary = (
  filters: OpenProjectPayrollAuditFilter,
) => {
  const summary: string[] = [];
  if (filters.month)
    summary.push(`Month: ${formatPayrollMonth(filters.month)}`);
  if (filters.fromMonth || filters.throughMonth)
    summary.push(
      `Period: ${filters.fromMonth ? formatPayrollMonth(filters.fromMonth) : "earliest"} – ${filters.throughMonth ? formatPayrollMonth(filters.throughMonth) : "latest"}`,
    );
  if (filters.userId) summary.push(`Employee #${filters.userId}`);
  if (filters.fieldKey) summary.push(`Field: ${words(filters.fieldKey)}`);
  if (filters.eventKind) summary.push(`Event: ${words(filters.eventKind)}`);
  if (filters.scope) summary.push(`Scope: ${words(filters.scope)}`);
  return summary;
};
import {
  openProjectPayrollAuditFilterSchema,
  type OpenProjectPayrollAuditFilter,
  type OpenProjectPayrollAuditLevel,
} from "../types/openproject";
